import { useCallback } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'

const FEED_TYPES = [
  {
    id: 'google_shopping',
    nameKey: 'feed.types.googleShopping',
    nameFallback: 'Google Shopping',
    descKey: 'feed.types.googleShoppingDesc',
    descFallback: 'Standard XML format used by Google Merchant Center',
    icon: (
      <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
      </svg>
    )
  },
  {
    id: 'facebook_catalog',
    nameKey: 'feed.types.facebookCatalog',
    nameFallback: 'Facebook Catalog',
    descKey: 'feed.types.facebookCatalogDesc',
    descFallback: 'Meta product feed for Facebook and Instagram shops',
    icon: (
      <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-1.414-.586m0 0L11 14h4a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2v4l.586-.586z" />
      </svg>
    )
  },
  // Custom XML feed type - hidden until backend implementation is complete
  // {
  //   id: 'custom',
  //   nameKey: 'feed.types.custom',
  //   nameFallback: 'Custom XML',
  //   descKey: 'feed.types.customDesc',
  //   descFallback: 'Define your own field mapping for custom XML feeds',
  //   icon: (...)
  // }
]

function FeedTypeSelector({ value, onChange, disabled = false }) {
  const { t } = useTranslation('ecommerce')

  const handleSelect = useCallback((typeId) => {
    if (!disabled) {
      onChange(typeId)
    }
  }, [disabled, onChange])

  return (
    <div className="space-y-3">
      <label className="block text-sm font-medium text-gray-700">
        {t('feed.typeLabel', 'Feed Type')}
      </label>
      <div
        role="radiogroup"
        aria-label={t('feed.typeLabel', 'Feed Type')}
        className="space-y-3"
      >
        {FEED_TYPES.map((type) => {
          const isSelected = value === type.id
          return (
            <label
              key={type.id}
              className={`
                relative flex items-start p-4 rounded-lg border-2 cursor-pointer
                transition-all duration-200
                ${isSelected
                  ? 'border-primary-500 bg-primary-50'
                  : 'border-gray-200 hover:border-gray-300 bg-white'
                }
                ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
              `}
            >
              <input
                type="radio"
                id={`feed-type-${type.id}`}
                name="feed-type"
                value={type.id}
                checked={isSelected}
                onChange={() => handleSelect(type.id)}
                disabled={disabled}
                className="sr-only"
                aria-label={t(type.nameKey, type.nameFallback)}
              />
              <div className={`
                flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center
                ${isSelected ? 'bg-primary-100 text-primary-600' : 'bg-gray-100 text-gray-500'}
              `}>
                {type.icon}
              </div>
              <div className="ml-4 flex-1">
                <span className={`
                  block text-sm font-medium
                  ${isSelected ? 'text-primary-900' : 'text-gray-900'}
                `}>
                  {t(type.nameKey, type.nameFallback)}
                </span>
                <span className={`
                  block text-sm mt-0.5
                  ${isSelected ? 'text-primary-700' : 'text-gray-500'}
                `}>
                  {t(type.descKey, type.descFallback)}
                </span>
              </div>
              {isSelected && (
                <div className="flex-shrink-0 ml-3">
                  <svg
                    className="h-5 w-5 text-primary-600"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                    aria-hidden="true"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
              )}
            </label>
          )
        })}
      </div>
    </div>
  )
}

FeedTypeSelector.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
}

export default FeedTypeSelector
